import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
	UntypedFormBuilder,
	UntypedFormGroup,
	Validators,
} from '@angular/forms';
import {
	DateAdapter,
	MAT_DATE_FORMATS,
	MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import {
	MAT_MOMENT_DATE_ADAPTER_OPTIONS,
	MomentDateAdapter,
} from '@angular/material-moment-adapter';

import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { Store } from '@ngrx/store';
import moment from 'moment';

import { BeneficiariesService } from '../../../../../modules/beneficiaries/beneficiaries.service';
import { BeneficiaryDependentDto } from '../../../../../modules/beneficiaries/dtos/beneficiary-dependent';
import { BeneficiaryDependentCreateDto } from '../../../../../modules/beneficiaries/dtos/beneficiary-dependent-create';
import { BeneficiaryDependentUpdateDto } from '../../../../../modules/beneficiaries/dtos/beneficiary-dependent-update';
import { SnackBarService } from '../../../../../shared/components/snack-bar/snack-bar.service';
import { Gender } from '../../../../../shared/enums/gender.enum';
import { TypeKinship } from '../../../../../shared/enums/type-kinship.enum';
import { MessageResponse } from '../../../../../shared/models/message-response.model';
import { DataTypeUtils } from '../../../../../utils/data-type.util';
import { PT_BR_DATE_FORMATS } from '../../../../../utils/date-formats.utils';
import { EnumUtils } from '../../../../../utils/enum.utils';
import { normalizeEmptyToNull } from '../../../../../utils/fields.utils';
import { PhoneUtils } from '../../../../../utils/phone-utils';
import { StringUtils } from '../../../../../utils/string.utils';
import {
	fetchCpfDependentStart,
	fetchCpfTitularStart,
	fetchEmailDependentStart,
	restoreFormReset,
} from '../../../state/organizations.actions';
import {
	selectCpfDependent,
	selectCpfTitular,
	selectEmailDependent,
} from '../../../state/organizations.selector';

@Component({
	providers: [
		{ provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
		{
			deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
			provide: DateAdapter,
			useClass: MomentDateAdapter,
		},
		{ provide: MAT_DATE_FORMATS, useValue: PT_BR_DATE_FORMATS },
	],
	selector: 'app-create-edit-dependent-modal',
	styleUrls: ['./create-edit-dependent-modal.component.scss'],
	templateUrl: './create-edit-dependent-modal.component.html',
})
export class CreateEditDependentModalComponent implements OnInit, OnDestroy {
	cpfPattern = '[0-9]{11}';
	dependentTypes = [
		{ label: 'Cônjuge', value: TypeKinship.CONJUGE },
		{ label: 'Filho', value: TypeKinship.FILHO },
		{ label: 'Enteado', value: TypeKinship.ENTEADO },
		{ label: 'Pai / Mãe', value: TypeKinship.PAI_MAE },
		{ label: 'Neto', value: TypeKinship.NETO },
		{ label: 'Irmão', value: TypeKinship.IRMAO },
		{ label: 'Avô / Avó', value: TypeKinship.AVO },
		{ label: 'Tio / Tia', value: TypeKinship.TIO },
		{ label: 'Sobrinho', value: TypeKinship.SOBRINHO },
		{ label: 'Bisneto', value: TypeKinship.BISNETO },
		{ label: 'Sogro', value: TypeKinship.SOGRO },
		{ label: 'Genro / Nora', value: TypeKinship.GENRO_NORA },
		{ label: 'Padrasto / Madrasta', value: TypeKinship.PADRASTO_MADRASTA },
		{ label: 'Outros', value: TypeKinship.OUTROS },
	];
	form: UntypedFormGroup;
	genders: Gender[] = Object.values(Gender);
	inputId: string = String(Math.random());
	isEdit: boolean = false;

	isLoading: boolean = false;

	phoneMask = (value: string): string => {
		this.form.get('phone')?.updateValueAndValidity();

		return value ? PhoneUtils.phoneMask(value) : '';
	};
	private createDependent$: Subscription = new Subscription();
	private searchDependent$: Subscription = new Subscription();
	private readonly unsubscribe$: Subject<void> = new Subject();

	private updateDependent$: Subscription = new Subscription();

	constructor(
		private readonly beneficiariesService: BeneficiariesService,
		private readonly fb: UntypedFormBuilder,
		private readonly store: Store,
		private readonly snackbarService: SnackBarService,
		public dialogRef: MatDialogRef<CreateEditDependentModalComponent>,
		@Inject(MAT_DIALOG_DATA)
		public data: { cpf: string; dependentId?: string; organizationId: string },
	) {
		this.form = this.fb.group({
			birthday: [null, [Validators.required]],
			cpf: [null, [Validators.required, Validators.pattern(this.cpfPattern)]],
			cpf_titular: [
				{ disabled: true, value: data.cpf },
				[Validators.required, Validators.pattern(this.cpfPattern)],
			],
			email: [null, [Validators.email]],
			gender: [null],
			inclusion_day: [null],
			marriageDate: [null],
			mothers_name: [null],
			name: [null, [Validators.required]],
			phone: [null, [Validators.pattern(DataTypeUtils.phone.pattern)]],
			type: [null, [Validators.required]],
		});
	}

	capitalize(value: string): string {
		return StringUtils.capitalize(value);
	}

	checkDependentType(): void {
		const marriageDateControl = this.form.get('marriageDate');

		if (this.form.get('type')?.value != TypeKinship.CONJUGE) {
			marriageDateControl?.disable();
			marriageDateControl?.setValue(null);
		} else {
			marriageDateControl?.enable();
		}
	}

	closeModal() {
		this.dialogRef.close({ reload: false });
	}

	createDependent(
		organizationId: string,
		request: BeneficiaryDependentCreateDto,
	): void {
		this.createDependent$ = this.beneficiariesService
			.createDependent({
				dto: request,
				organizationId: organizationId,
			})
			.subscribe(
				async (response: MessageResponse) => {
					this.snackbarService.showSnack(response?.message, response?.type);
					this.dialogRef.close({ reload: true });
					this.isLoading = false;
				},
				(error) => {
					this.snackbarService.showSnack(error.error?.message, 'error');
					this.isLoading = false;
				},
			);
	}

	editDependent(
		dependentId: string,
		request: BeneficiaryDependentUpdateDto,
	): void {
		this.updateDependent$ = this.beneficiariesService
			.updateDependent({
				dependentId: dependentId,
				dto: request,
			})
			.subscribe(
				async (response: MessageResponse) => {
					this.snackbarService.showSnack(response?.message, response?.type);
					this.dialogRef.close({ reload: true });
					this.isLoading = false;
				},
				(error) => {
					this.snackbarService.showSnack(error.error?.message, 'error');
					this.isLoading = false;
				},
			);
	}

	ngOnDestroy(): void {
		this.unsubscribe$.next();
		this.unsubscribe$.complete();
		this.searchDependent$.unsubscribe();
		this.createDependent$.unsubscribe();
		this.updateDependent$.unsubscribe();
		this.store.dispatch(restoreFormReset());
	}

	ngOnInit(): void {
		this.form.get('marriageDate')?.disable();
		if (this.data.dependentId) {
			this.isLoading = true;
			this.searchDependent$ = this.beneficiariesService
				.searchDependent({
					dependentId: this.data.dependentId,
					organizationId: this.data.organizationId,
				})
				.subscribe(
					async (dependent: BeneficiaryDependentDto) => {
						if (dependent) {
							this.isEdit = true;
							this.form.patchValue({
								birthday: dependent.birthday,
								cpf: dependent.user.cpf,
								email: dependent.user.email,
								gender: dependent.gender?.toUpperCase(),
								inclusion_day: dependent.professional_data.plan_inclusion_date,
								marriageDate: dependent.marriageDate,
								mothers_name: dependent.mothers_name,
								name: dependent.user.name,
								phone: DataTypeUtils.phone.sanitizePhoneNumber(
									dependent.user?.phone,
								),
								type: dependent.typeKinship,
							});
							this.form.controls.cpf.disable();
							if (this.form.get('type')?.value === TypeKinship.CONJUGE) {
								this.form.get('marriageDate')?.enable();
							}
						}
						this.isLoading = false;
					},
					() => {
						this.isLoading = false;
					},
				);
		}
	}

	onContinue(): void {
		this.isLoading = true;

		if (this.form.valid) {
			if (!this.data.dependentId) {
				this.form.get('cpf_titular')!.enable();
			}

			const request = this.form.getRawValue();
			request.email = normalizeEmptyToNull(request.email);
			request.phone = normalizeEmptyToNull(request.phone);
			request.birthday = moment(request.birthday).format(
				'YYYY-MM-DD hh:mm:ssZZ',
			);
			request.inclusion_day = request.inclusion_day
				? moment(request.inclusion_day).format('YYYY-MM-DD hh:mm:ssZZ')
				: null;
			request.marriageDate = request.marriageDate
				? moment(request.marriageDate).format('YYYY-MM-DD hh:mm:ssZZ')
				: null;
			this.form.get('cpf_titular')!.disable();
			this.form.get('cpf')!.disable();

			if (this.data.dependentId) {
				const kinshipEnum = EnumUtils.getEnumValue(TypeKinship, request.type);

				if (kinshipEnum === TypeKinship.OUTROS) {
					delete request.type;
					request.typeKinship = TypeKinship.OUTROS;
				} else {
					request.type =
						kinshipEnum !== undefined
							? EnumUtils.getKinshipNumericValue(kinshipEnum)
							: null;
				}
				this.editDependent(
					this.data.dependentId,
					request as BeneficiaryDependentUpdateDto,
				);
			} else {
				request.type = EnumUtils.getEnumValue(TypeKinship, request.type);
				this.createDependent(
					this.data.organizationId,
					request as BeneficiaryDependentCreateDto,
				);
			}
		}
	}

	onCpfDependentBlur(): void {
		if (this.form.get('cpf')!.valid) {
			this.store.dispatch(
				fetchCpfDependentStart({
					cpf: this.form.get('cpf')!.value,
					orgId: this.data.organizationId,
				}),
			);
			this.store
				.select(selectCpfDependent)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (result) {
						this.form.get('cpf')!.updateValueAndValidity();
					} else if (result === false) {
						this.form.get('cpf')!.setErrors({ minlength: true });
					}
				});
		}
	}

	onCpfTitularBlur(): void {
		if (this.form.get('cpf_titular')!.valid) {
			this.store.dispatch(
				fetchCpfTitularStart({
					cpf: this.form.get('cpf_titular')!.value,
					orgId: this.data.organizationId,
				}),
			);
			this.store
				.select(selectCpfTitular)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (result) {
						this.form.get('cpf_titular')!.updateValueAndValidity();
					} else if (result === false) {
						this.form.get('cpf_titular')!.setErrors({ minlength: true });
					}
				});
		}
	}

	onEmailBlur(): void {
		const email = this.form.get('email');

		if (email!.valid && email!.value) {
			this.store.dispatch(
				fetchEmailDependentStart({
					email: email!.value,
					orgId: this.data.organizationId,
				}),
			);
			this.store
				.select(selectEmailDependent)
				.pipe(takeUntil(this.unsubscribe$))
				.subscribe((result) => {
					if (result) {
						email!.updateValueAndValidity();
					} else if (result === false) {
						email!.setErrors({ minlength: true });
					}
				});
		}
	}

	updateValidation(): void {
		for (const control in this.form.controls) {
			this.form.get(control)!.markAsTouched();
			this.form.get(control)!.updateValueAndValidity({ onlySelf: true });
		}
	}
}
